import type { UseUserDropdownMenuOptionsParams } from '../UserDropdownMenu.types';
import { Auth0Context } from '@auth0/auth0-react';
import type { ListPopoverOption } from 'Components/shared/ListPopover/ListPopoverItem/index.types';
import { HELP_URL, IS_ELECTRON, NODE_ENV_LOCAL_OR_DEVELOPMENT } from 'Constants/env';
import { REFRESH_TOKEN } from 'Constants/featureFlags';
import useFeatureFlags from 'Hooks/featureFlags/useFeatureFlags';
import { MobXProviderContext } from 'mobx-react';
import { useReportIssue } from 'Modules/reportIssue/hooks/useReportIssue';
import React from 'react';
import { useNavigate } from 'react-router';
import type { RootStoreProps } from 'Stores/RootStore.types';
import {
  ANALYTICS_EVENT_DOWNLOAD_DESKTOP_APP_CLICKED,
  ANALYTICS_EVENT_SEND_FEEDBACK_CLICKED,
  pushToGTMDataLayer,
} from 'Utils/analytics';
import {
  downloadDesktopApp,
  openFeedbackSurvey,
} from 'Utils/globalUserActions';
import { bugsnagClient } from 'Utils/logUtils';

const REFRESH_TOKEN_FLAGS = [REFRESH_TOKEN];

export const useUserDropdownMenuOptions = ({
  setShowMessageStatusModal,
  testid,
}: UseUserDropdownMenuOptionsParams): ListPopoverOption[] => {
  const { person } = React.useContext<RootStoreProps>(MobXProviderContext);
  const auth0 = React.useContext(Auth0Context);
  const navigate = useNavigate();
  const { [REFRESH_TOKEN]: isRefreshTokenEnabled } =
    useFeatureFlags(REFRESH_TOKEN_FLAGS);
  const { openReportIssueModal } = useReportIssue();

  return React.useMemo(() => {
    const handleLogoutClick = () => {
      void person.logout();
    };

    const handleRefreshTokenClick = async () => {
      try {
        person.setRefreshingToken(true);
        await auth0.getAccessTokenSilently({ ignoreCache: true });
      } catch (error) {
        bugsnagClient.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'handleRefreshTokenClick';
          event.addMetadata('custom', {
            function: 'getAccessTokenSilently',
          });
        });
        await person.initiateAutoLogout(true);
      } finally {
        person.setRefreshingToken(false);
      }
    };

    /** For simulating token expiration in local/dev builds */
    const handleTokenExpiredClick = () => {
      person.setSimulateTokenExpiration(true);
    };

    const handleSendFeedbackClick = () => {
      pushToGTMDataLayer(ANALYTICS_EVENT_SEND_FEEDBACK_CLICKED);
      openFeedbackSurvey();
    };

    const handleDownloadDesktopAppClick = () => {
      pushToGTMDataLayer(ANALYTICS_EVENT_DOWNLOAD_DESKTOP_APP_CLICKED);
      downloadDesktopApp();
    };

    const handleHelpClick = () => {
      window.open(HELP_URL, '_blank', 'noopener,noreferrer');
    };

    const handleReportIssueClick = () => {
      void openReportIssueModal();
    };

    const options: ListPopoverOption[] = [
      {
        text: 'Status & availability',
        testId: `${testid}-dropdownOptionAvailability`,
        onClick: () => setShowMessageStatusModal(true),
      },
      {
        text: 'Settings',
        testId: `${testid}-dropdownOptionSettings`,
        onClick: () => navigate('/settings'),
      },
      {
        text: 'Send feedback',
        testId: `${testid}-dropdownOptionSendFeedback`,
        onClick: handleSendFeedbackClick,
      },
      {
        text: 'Report an issue',
        testId: `${testid}-dropdownOptionReportIssue`,
        onClick: handleReportIssueClick,
      },
    ];

    if (!IS_ELECTRON) {
      options.push({
        text: 'Download desktop app',
        testId: `${testid}-dropdownOptionDownloadDesktopApp`,
        onClick: handleDownloadDesktopAppClick,
      });
    }

    if (HELP_URL) {
      options.push({
        text: 'Help',
        testId: `${testid}-dropdownOptionHelp`,
        onClick: handleHelpClick,
      });
    }

    options.push({
      text: 'Logout',
      testId: `${testid}-dropdownOptionLogout`,
      destructive: true,
      onClick: handleLogoutClick,
    });

    if (isRefreshTokenEnabled) {
      options.push({
        text: 'Refresh token',
        testId: `${testid}-dropdownOptionRefreshToken`,
        onClick: () => void handleRefreshTokenClick(),
      });
    }

    if (NODE_ENV_LOCAL_OR_DEVELOPMENT) {
      options.push({
        text: 'Simulate token expiration',
        testId: `${testid}-dropdownOptionTokenExpired`,
        onClick: handleTokenExpiredClick,
      });
    }

    return options;
  }, [
    auth0,
    isRefreshTokenEnabled,
    navigate,
    openReportIssueModal,
    person,
    setShowMessageStatusModal,
    testid,
  ]);
};
